/**
 * In-app reference pages.
 *
 * These say the same things as `docs/iphone-shortcuts.md` and
 * `docs/privacy-and-security.md`, because the person setting up a Shortcut on
 * their phone is not going to be reading a markdown file on a laptop.
 */
import { Link } from 'react-router-dom';
import { Notice } from '../components/primitives.js';

export function ShortcutsGuide(): JSX.Element {
  return (
    <article className="stack-lg" style={{ marginTop: 'var(--space-3)' }}>
      <Notice tone="warning">
        Before anything else, the honest limit: no app on iOS can read another app&rsquo;s
        notifications, and no ordinary app can read your Apple Pay history. Everything below works
        by <strong>you</strong> triggering a Shortcut and typing the amount. There is no way around
        that on iPhone, and anything claiming otherwise is either using private APIs or making it up.
      </Notice>

      <section className="card">
        <h2 className="section__title">Before you start</h2>
        <ol className="stack small" style={{ marginTop: 'var(--space-3)', paddingLeft: '1.2rem', listStyle: 'decimal' }}>
          <li>Deploy the Worker (see the README) and note its address.</li>
          <li>
            Set the ingestion token: <code>wrangler secret put INGEST_TOKEN</code>. Use a long
            random string. Keep it in your password manager, not in a note.
          </li>
          <li>Open the app once, create your account, then paste the Worker address in Settings → Cloud sync.</li>
        </ol>
      </section>

      <section className="card">
        <h2 className="section__title">1. After tapping an Apple Wallet card</h2>
        <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
          iOS can run a Shortcut when you use a specific Wallet card. It tells the Shortcut that a
          transaction happened -- it does not reliably hand over the amount or the shop, so the
          Shortcut asks you.
        </p>
        <ol className="stack small" style={{ marginTop: 'var(--space-3)', paddingLeft: '1.2rem', listStyle: 'decimal' }}>
          <li>Shortcuts → Automation → New → Transaction.</li>
          <li>Choose &ldquo;When I Tap&rdquo; and pick the card you actually pay with.</li>
          <li>Set it to run immediately, without asking, if your iOS version offers that.</li>
          <li>Add &ldquo;Ask for Input&rdquo; → Number → &ldquo;How much?&rdquo;</li>
          <li>Add a second &ldquo;Ask for Input&rdquo; → Text → &ldquo;Where?&rdquo; and allow it to be left blank.</li>
          <li>
            Add &ldquo;Get Contents of URL&rdquo; pointing at
            <code> https://your-worker/api/ingest/shortcut</code>, method POST, with an
            <code> Authorization: Bearer YOUR_TOKEN</code> header and a JSON body containing
            amount, merchant, <code>source: apple-pay-shortcut</code> and a unique
            <code> idempotencyKey</code>.
          </li>
          <li>Finish with &ldquo;Show Notification&rdquo; so you can see whether it worked.</li>
        </ol>
        <p className="small muted" style={{ marginTop: 'var(--space-3)' }}>
          The full JSON body, with the exact field names, is in{' '}
          <code>docs/iphone-shortcuts.md</code>.
        </p>
      </section>

      <section className="card">
        <h2 className="section__title">2. After closing MAE or TNG eWallet</h2>
        <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
          Since the notification itself is off limits, the workaround is to catch the moment you
          leave the app: an automation triggered when MAE or TNG eWallet closes asks whether you
          just spent something.
        </p>
        <ol className="stack small" style={{ marginTop: 'var(--space-3)', paddingLeft: '1.2rem', listStyle: 'decimal' }}>
          <li>Shortcuts → Automation → New → App.</li>
          <li>Choose MAE by Maybank (or TNG eWallet) and select &ldquo;Is Closed&rdquo;.</li>
          <li>Add &ldquo;Ask for Input&rdquo; → Text → &ldquo;Did you just spend money?&rdquo; with Yes and No.</li>
          <li>Add an If block: if the answer is not Yes, Stop the Shortcut.</li>
          <li>If it is Yes, ask for the amount and the merchant, then POST as above.</li>
          <li>
            Use <code>maybank-quick-capture</code> or <code>tng-quick-capture</code> as the source so
            the app can show you where the record came from.
          </li>
        </ol>
      </section>

      <section className="card">
        <h2 className="section__title">3. The fastest one: a Home Screen button</h2>
        <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
          No automation at all. A Shortcut that asks for an amount and opens this app&rsquo;s
          <code> /capture</code> link, added to your Home Screen, to Back Tap, or to the Action
          Button. It works offline, because the app saves locally.
        </p>
        <p className="small" style={{ marginTop: 'var(--space-3)' }}>
          The URL shape is{' '}
          <code>https://your-app/capture?source=apple-pay-shortcut&amp;amount=1290&amp;merchant=Kopi%20Kita</code>
          , where the amount is in <strong>sen</strong>. The app always shows you the form before
          saving anything.
        </p>
      </section>

      <p className="small muted">
        Full copy-and-paste instructions, including the JSON body and how to generate an idempotency
        key, are in <code>docs/iphone-shortcuts.md</code> in the project. <Link to="/settings">Back to Settings</Link>.
      </p>
    </article>
  );
}

export function PrivacyPage(): JSX.Element {
  return (
    <article className="stack-lg" style={{ marginTop: 'var(--space-3)' }}>
      <section className="card">
        <h2 className="section__title">Where your data is</h2>
        <ul className="stack small" style={{ marginTop: 'var(--space-3)' }}>
          <li>
            <strong>On this device, always.</strong> Everything you record lives in this
            browser&rsquo;s IndexedDB storage, under this app&rsquo;s own origin. No other site can
            read it.
          </li>
          <li>
            <strong>On your Cloudflare Worker, only if you switch sync on.</strong> That Worker and
            its D1 database are yours; nobody else is involved.
          </li>
          <li>
            <strong>Nowhere else.</strong> There is no analytics, no tracking, no advertising, no
            crash reporting and no third-party service of any kind. The app loads nothing from any
            other origin.
          </li>
        </ul>
      </section>

      <section className="card">
        <h2 className="section__title">What this app will never ask for</h2>
        <ul className="stack small" style={{ marginTop: 'var(--space-3)' }}>
          <li>Your bank username or password.</li>
          <li>A PIN, a CVV, a card number or a card expiry date.</li>
          <li>An OTP, a TAC, or a Secure2u approval.</li>
        </ul>
        <p className="small muted" style={{ marginTop: 'var(--space-3)' }}>
          There is nowhere in the database to put any of those, and no screen that asks. The only
          password the app knows is the one protecting your own cloud copy, and it is stored as a
          salted PBKDF2 hash.
        </p>
      </section>

      <section className="card">
        <h2 className="section__title">What it cannot do</h2>
        <ul className="stack small" style={{ marginTop: 'var(--space-3)' }}>
          <li>It cannot read notifications from MAE, Maybank, TNG eWallet or anything else.</li>
          <li>It cannot read your Apple Pay transaction history.</li>
          <li>It cannot connect to your bank.</li>
        </ul>
        <p className="small muted" style={{ marginTop: 'var(--space-3)' }}>
          iOS does not give ordinary apps access to another app&rsquo;s notifications, and this app
          uses no private APIs, no screen scraping and no accessibility tricks to get around that.
          Everything it knows, you or a Shortcut you wrote put there.
        </p>
      </section>

      <section className="card">
        <h2 className="section__title">Receipts and images</h2>
        <p className="small" style={{ marginTop: 'var(--space-2)' }}>
          Receipt text is parsed on this device. The original image is not uploaded, and is not even
          kept, unless you explicitly turn on cloud attachment storage in Settings -- which is off by
          default.
        </p>
      </section>

      <section className="card">
        <h2 className="section__title">Getting your data out, or getting rid of it</h2>
        <ul className="stack small" style={{ marginTop: 'var(--space-3)' }}>
          <li>Settings → Export gives you a complete CSV or a full JSON backup, any time.</li>
          <li>Settings → Delete all data erases everything on this device, after you type DELETE.</li>
          <li>Settings → Cloud sync → Erase cloud copy does the same to the Worker&rsquo;s database.</li>
        </ul>
      </section>

      <p className="small muted">
        The longer version, including the security choices behind all of this, is in{' '}
        <code>docs/privacy-and-security.md</code>. <Link to="/settings">Back to Settings</Link>.
      </p>
    </article>
  );
}
